import { useState, useEffect, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, Video, Phone, MapPin, ChevronLeft, CalendarCheck, Loader2 } from "lucide-react";
import { useSeo } from "@/lib/useSeo";
import { getBookingConfig, getAvailability, requestBooking } from "@/lib/api";
import { track } from "@/lib/analytics";
import { PageHeader } from "@/components/Editorial";
import { IMG } from "@/lib/images";
import { parisDate, calendarDays, millisecondsToParisMidnight } from "@/lib/bookingDates";


const MODES = [
  { key: "visio", label: "Visioconférence", Icon: Video },
  { key: "telephone", label: "Téléphone", Icon: Phone },
  { key: "bureau", label: "À mon bureau", Icon: MapPin },
];
const MODE_LABEL = { visio: "Visioconférence", telephone: "Téléphone", bureau: "À mon bureau" };
const DOW = ["lun.", "mar.", "mer.", "jeu.", "ven.", "sam.", "dim."];

const STEPS = ["Prestation", "Mode", "Date", "Heure", "Coordonnées"];

export default function RendezVous() {
const { data: config, isPlaceholderData: configPending, isError: configError, refetch: refreshConfig } = useQuery({
queryKey: ["booking-config"],
queryFn: getBookingConfig,
retry: false,
placeholderData: {
prestations: [
{ name: "Voyance", price: 50 },
{ name: "Contact défunt", price: 80 },
{ name: "Onirothérapie", price: 70 },
{ name: "Numérologie", price: 45 },
{ name: "Lecture d’âme", price: 35 },
{ name: "Hypnose Ericksonienne", price: 60 },
],
},
});
  const [step, setStep] = useState(0);
  const [prestation, setPrestation] = useState(null);
  const [mode, setMode] = useState(null);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [payment, setPayment] = useState("");
  const [avail, setAvail] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", consent: false, hp: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const [today, setToday] = useState(parisDate);
  const [priceAcknowledged, setPriceAcknowledged] = useState(false);
  const availabilityRequest = useRef(0);
  const submitting = useRef(false);
  const requestIdentity = useRef(null);

  useSeo({ title: "Demander un rendez-vous", description: "Envoyez une demande de consultation à Dorothée Lancelot. Le rendez-vous n’est confirmé qu’après son acceptation personnelle.", path: "/rendez-vous" });

  useEffect(() => { track("rdv_open", { source: "page" }); }, []);

  useEffect(() => {
    let timer;
    const refresh = () => {
      setToday(parisDate());
      clearTimeout(timer);
      timer = setTimeout(refresh, millisecondsToParisMidnight() + 20);
    };
    refresh();
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => { clearTimeout(timer); window.removeEventListener("focus", refresh); document.removeEventListener("visibilitychange", refresh); };
  }, []);
  useEffect(() => {
    if (date && date <= today) {
      availabilityRequest.current += 1; setDate(null); setTime(null); setAvail(null);
      setPriceAcknowledged(false); setStep(2);
    }
  }, [today, date]);
  const days = useMemo(() => config ? calendarDays(today, config.work_days || [], config.blocked_dates || []) : [], [config, today]);

  const surcharge = avail && time ? (avail.slots.find((s) => s.time === time)?.surcharge || 0) : 0;
  const base = prestation?.price || 0;
  const total = base + surcharge;
  const surchargeReason = avail?.slots.find(s => s.time === time)?.surcharge_reason || "";
  useEffect(() => { setPriceAcknowledged(false); }, [date, time, prestation, total]);

  const chooseDate = async (key) => {
    if (key <= parisDate() || configPending || configError) return;
    const request = ++availabilityRequest.current;
    setDate(key); setTime(null); setAvail(null); setLoadingSlots(true);
    try {
      const result = await getAvailability(key);
      if (request === availabilityRequest.current) setAvail(result);
    } catch {
      if (request === availabilityRequest.current) toast.error("Impossible de vérifier les disponibilités. Réessayez.");
    } finally { if (request === availabilityRequest.current) setLoadingSlots(false); }
  };
  

  const submit = async () => {
    if (submitting.current) return;
    if (!date || date <= parisDate() || !time) { toast.error("Choisissez un créneau à partir de demain, en heure de Paris."); setStep(2); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) { toast.error("E-mail invalide"); return; }
    if (!payment) { toast.error("Merci de choisir un mode de paiement"); return; }
    if (!form.consent || !priceAcknowledged) { toast.error("Merci d’accepter la politique et de reconnaître le tarif total."); return; }
    submitting.current = true; setSending(true);
    try {
      const payload = { ...form, prestation: prestation.name, mode, date, time,
        payment_method: payment, price_acknowledged: priceAcknowledged, acknowledged_total: total };
      const signature = JSON.stringify(payload);
      if (requestIdentity.current?.signature !== signature) {
        requestIdentity.current = { signature, key: crypto.randomUUID() };
      }
      const result = await requestBooking({ ...payload, idempotency_key: requestIdentity.current.key });
      if (!result.appointment) throw new Error("Demande non enregistrée");
      setDone(result.appointment);
    } catch (error) {
      toast.error(error.response?.data?.detail || "La demande n’a pas été enregistrée. Veuillez réessayer.");
      if (error.response?.status === 409) { setTime(null); setPriceAcknowledged(false); await refreshConfig(); setStep(2); }
    } finally { submitting.current = false; setSending(false); }
  };

  const inp = "w-full bg-[#050814] border border-[rgba(198,167,106,0.3)] px-3 py-2.5 text-[#F3E9D2] text-sm focus:outline-none focus:border-[#C6A76A]";

  if (done) {
    return (
      <div>
        <PageHeader eyebrow="En attente de validation" title="DEMANDE ENREGISTRÉE — EN ATTENTE DE VALIDATION" image={IMG.candle} />
        <section className="dl-container py-16 max-w-2xl" data-testid="rdv-success">
          <div className="border border-[rgba(198,167,106,0.35)] bg-[#0B1020] p-8">
            <div className="flex items-center gap-3 text-[#D4B978]"><CalendarCheck size={26} /><span className="serif text-2xl text-[#F3E9D2]">Statut : en attente de validation</span></div>
            <p className="text-[#F3E9D2]/70 mt-4 leading-relaxed">
              Votre demande de rendez-vous a bien été enregistrée, mais elle reste en attente de validation personnelle. Pensez à consulter votre boîte de réception ainsi que vos courriers indésirables (SPAM). Sans confirmation explicite, votre rendez-vous n'est pas validé.
            </p>
            <div className="mt-6 grid grid-cols-2 gap-y-2 text-sm text-[#F3E9D2]/80">
              <span className="text-[#F3E9D2]/50">Prestation</span><span>{done.prestation}</span>
              <span className="text-[#F3E9D2]/50">Mode</span><span>{MODE_LABEL[done.mode]}</span>
              <span className="text-[#F3E9D2]/50">Date</span><span>{done.date} · {done.time}</span>
              <span className="text-[#F3E9D2]/50">Tarif</span><span className="text-[#D4B978]">{done.total} €{done.surcharge ? ` (dont +${done.surcharge} € supplément)` : ""}</span>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div>
      <PageHeader eyebrow="Demande de rendez-vous" title="Prendre rendez-vous" intro="Choisissez votre consultation et envoyez une demande. Seule l’acceptation personnelle de Dorothée Lancelot confirme le rendez-vous." image={IMG.libraryCandle} />
      <section className="dl-container py-12 max-w-3xl" data-testid="rdv-page">
        <div className="dl-container pt-8">
<div className="dl-card p-5 flex flex-col md:flex-row items-center gap-5">
<img
src="/Maine-Coon-calendrier.jpeg"
alt="Maine Coon"
className="w-32 h-32 object-cover rounded-2xl"
/>

<div>
<div className="dl-eyebrow mb-2">Votre rendez-vous</div>
<p className="text-[#F3E9D2]/80">
Choisissez tranquillement votre prestation, votre date et votre créneau.
Les rendez-vous commencent à partir de 13h30.
</p>
</div>
</div>
</div>
        {/* Progress */}
        <div className="flex flex-wrap gap-2 mb-10">
          {STEPS.map((s, i) => (
            <div key={s} className={`flex items-center gap-2 text-xs tracking-wide uppercase px-3 py-1.5 border ${i === step ? "border-[#C6A76A] text-[#D4B978]" : i < step ? "border-[rgba(198,167,106,0.3)] text-[#F3E9D2]/70" : "border-white/10 text-[#F3E9D2]/35"}`}>
              {i < step ? <Check size={13} /> : <span>{i + 1}</span>} {s}
            </div>
          ))}
        </div>

        {step > 0 && (
          <button onClick={() => setStep(step - 1)} className="dl-link text-sm mb-6 inline-flex items-center gap-1" data-testid="rdv-back"><ChevronLeft size={15} /> Retour</button>
        )}

        {/* Step 0: prestation */}
        {configPending && <p role="status">Vérification des prestations et des tarifs…</p>}
        {configError && <div role="alert" className="border border-[rgba(198,167,106,0.35)] p-4 mb-6">
          <p>La réservation en ligne est temporairement indisponible. Aucune demande n’a été envoyée.</p>
          <p className="mt-2">Vous pouvez contacter Dorothée par <a className="dl-link" href="mailto:dorothee-medium@hotmail.fr">e-mail</a> ou au <a className="dl-link" href="tel:+33650216371">06 50 21 63 71</a>.</p>
          <button type="button" className="dl-btn dl-btn-outline mt-3" onClick={() => refreshConfig()}>Réessayer la vérification</button>
        </div>}
        {step === 0 && (
          <div className="space-y-3" data-testid="rdv-step-prestation">
            {(config?.prestations || []).map((p) => (
              <button key={p.name} disabled={configPending || configError} data-testid={`rdv-prestation-${p.name}`} onClick={() => { setPrestation(p); setStep(1); }}
                className={`w-full text-left border p-4 flex justify-between items-center hover:border-[#C6A76A] hover:bg-white/[0.03] transition-colors ${prestation?.name === p.name ? "border-[#C6A76A]" : "border-[rgba(198,167,106,0.25)]"}`}>
                <span><span className="serif text-lg text-[#F3E9D2]">{p.name}</span><span className="block text-xs text-[#F3E9D2]/50 mt-1">{p.duration}{p.note ? ` · ${p.note}` : ""}</span></span>
                <span className="text-[#D4B978] font-medium">{p.price} €</span>
              </button>
            ))}
          </div>
        )}

        {/* Step 1: mode */}
        {step === 1 && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="rdv-step-mode">
            {MODES.map((m) => (
              <button key={m.key} data-testid={`rdv-mode-${m.key}`} onClick={() => { setMode(m.key); setStep(2); }}
                className={`border p-6 flex flex-col items-center gap-3 hover:border-[#C6A76A] hover:bg-white/[0.03] transition-colors ${mode === m.key ? "border-[#C6A76A]" : "border-[rgba(198,167,106,0.25)]"}`}>
                <m.Icon size={26} className="text-[#C6A76A]" /><span className="text-[#F3E9D2]">{m.label}</span>
              </button>
            ))}
          </div>
        )}

        {/* Step 2: date */}
        {step === 2 && (
          <div data-testid="rdv-step-date">
            <p className="text-sm text-[#F3E9D2]/70 mb-4">Les créneaux du jour sont indisponibles. Première date possible : demain, selon les disponibilités (Europe/Paris).</p>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {days.map((d) => (
                <button key={d.key} disabled={!d.open || configPending || configError} data-testid={`rdv-date-${d.key}`}
                  onClick={() => { chooseDate(d.key); setStep(3); }}
                  className={`p-2.5 text-center border transition-colors ${d.open ? "border-[rgba(198,167,106,0.3)] text-[#F3E9D2]/85 hover:border-[#C6A76A]" : "border-white/5 text-[#F3E9D2]/20 cursor-not-allowed"}`}>
                  <span className="block text-[0.6rem] uppercase">{DOW[d.dow]}</span>
                  <span className="block text-lg serif">{d.d.getUTCDate()}</span>
                  <span className="block text-[0.55rem] text-[#F3E9D2]/40">{d.d.toLocaleDateString("fr-FR", { month: "short", timeZone: "UTC" })}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 3: time */}
        {step === 3 && (
          <div data-testid="rdv-step-time">
            <p className="text-[#F3E9D2]/60 text-sm mb-4">Créneaux pour le {date}{avail?.is_holiday ? " (jour férié — supplément)" : ""}</p>
            {loadingSlots ? (
              <p className="text-[#F3E9D2]/50 inline-flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Chargement…</p>
            ) : !avail?.slots?.some((s) => s.available) ? (
              <p className="text-[#F3E9D2]/50 italic">Aucun créneau disponible ce jour. <button className="dl-link" onClick={() => setStep(2)}>Choisir une autre date</button></p>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                {avail.slots.map((s) => (
                  <button key={s.time} disabled={!s.available || date <= today} data-testid={`rdv-slot-${s.time}`}
                    onClick={() => { setTime(s.time); setStep(4); }}
                    className={`p-2.5 border text-sm transition-colors ${!s.available ? "border-white/5 text-[#F3E9D2]/20 line-through cursor-not-allowed" : time === s.time ? "border-[#C6A76A] text-[#D4B978]" : "border-[rgba(198,167,106,0.3)] text-[#F3E9D2]/85 hover:border-[#C6A76A]"}`}>
                    {s.time}{s.surcharge ? <span className="block text-[0.55rem] text-[#D4B978]">+{s.surcharge} €</span> : null}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 4: coordinates + recap */}
        {step === 4 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-testid="rdv-step-coords">
            <div className="space-y-3.5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                <div><label className="dl-eyebrow block mb-1.5" style={{ fontSize: "0.55rem" }}>Prénom et nom</label><input data-testid="rdv-name" className={inp} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div><label className="dl-eyebrow block mb-1.5" style={{ fontSize: "0.55rem" }}>Téléphone</label><input data-testid="rdv-phone" className={inp} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              </div>
              <div><label className="dl-eyebrow block mb-1.5" style={{ fontSize: "0.55rem" }}>E-mail *</label><input data-testid="rdv-email" type="email" className={inp} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><label className="dl-eyebrow block mb-1.5" style={{ fontSize: "0.55rem" }}>Message (facultatif)</label><textarea data-testid="rdv-message" rows={3} className={inp} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} /></div>
              <input tabIndex={-1} autoComplete="off" value={form.hp} onChange={(e) => setForm({ ...form, hp: e.target.value })} style={{ position: "absolute", left: "-9999px", opacity: 0 }} aria-hidden="true" />
              <label className="flex items-start gap-2.5 text-xs text-[#F3E9D2]/55 cursor-pointer">
                <input data-testid="rdv-consent" type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="mt-0.5 accent-[#C6A76A]" />
                <span>J'accepte la <a href="/confidentialite" className="dl-link">politique de confidentialité</a>. Mes données servent uniquement à traiter ma demande.</span>
              </label>
              <div className="pt-4">
<div className="dl-eyebrow mb-3">Mode de paiement</div>

<div className="flex flex-wrap gap-2">
{(mode === "bureau"
? ["Espèces", "Chèque"]
: ["Wero", "Virement", "PayPal"]
).map((p) => (
<button
key={p}
type="button"
onClick={() => setPayment(p)}
className={`border px-4 py-3 ${
payment === p
? "border-[#D4B978] text-[#D4B978]"
: "border-white/20 text-[#F3E9D2]"
}`}
>
{p}
</button>
))}
</div>
</div>
            </div>

            <div className="border border-[rgba(198,167,106,0.3)] bg-[#0B1020] p-6 h-fit">
              <div className="dl-eyebrow mb-4">Récapitulatif</div>
              <div className="grid grid-cols-2 gap-y-2 text-sm text-[#F3E9D2]/80">
                <span className="text-[#F3E9D2]/50">Prestation</span><span>{prestation?.name}</span>
                <span className="text-[#F3E9D2]/50">Mode</span><span>{MODE_LABEL[mode]}</span>
                <span className="text-[#F3E9D2]/50">Paiement</span><span>{payment}</span>
                <span className="text-[#F3E9D2]/50">Date</span><span>{date}</span>
                <span className="text-[#F3E9D2]/50">Heure</span><span>{time}</span>
                <span className="text-[#F3E9D2]/50">Tarif de la prestation</span><span>{base} €</span>
                {surcharge > 0 && (<><span className="text-[#F3E9D2]/50">Supplément {surchargeReason}</span><span className="text-[#D4B978]">+{surcharge} €</span></>)}
              </div>
              <div className="flex justify-between items-center mt-4 pt-4 border-t border-[rgba(198,167,106,0.2)]">
                <span className="serif text-lg text-[#F3E9D2]">TOTAL DE LA CONSULTATION</span><span data-testid="rdv-total" className="serif text-2xl text-[#D4B978]">{total} €</span>
              </div>
              <div role="note" className="mt-5 border border-[#C6A76A] p-4 text-sm text-[#F3E9D2]">
                <strong>ATTENTION : VOTRE RENDEZ-VOUS N'EST PAS ENCORE CONFIRMÉ.</strong>
                <p className="mt-3">Vous effectuez une demande de rendez-vous. Celui-ci sera confirmé uniquement après validation personnelle de Dorothée Lancelot. Sans confirmation explicite de sa part, merci de ne pas vous présenter au rendez-vous. Lorsque vous attendez la confirmation, pensez à vérifier votre boîte de réception et vos courriers indésirables (SPAM).</p>
              </div>
              <label className="flex items-start gap-2 mt-4 text-sm text-[#F3E9D2]">
                <input type="checkbox" data-testid="rdv-price-consent" checked={priceAcknowledged} onChange={e => setPriceAcknowledged(e.target.checked)} />
                <span>J’ai pris connaissance du tarif total de {total} € et du fait que ma demande n’est pas encore confirmée.</span>
              </label>
              <button data-testid="rdv-submit" onClick={submit} disabled={sending || !form.consent || !priceAcknowledged || !payment} className="dl-btn dl-btn-solid w-full justify-center mt-6 disabled:opacity-50">
                {sending ? <><Loader2 size={15} className="animate-spin" /> Envoi…</> : "ENVOYER MA DEMANDE DE RENDEZ-VOUS"}
              </button>
              <p className="text-center text-[11px] text-[#F3E9D2]/45 mt-3">Votre demande sera effective uniquement après validation par Dorothée Lancelot.</p>
            </div>
          </div>
        )}
      </section>
      <section id="avis-google" aria-labelledby="avis-google-title" className="dl-container pb-16 max-w-3xl scroll-mt-28">
        <div className="border border-[rgba(198,167,106,0.3)] bg-[#0B1020] p-6 md:p-8 text-center">
          <div className="dl-eyebrow mb-3">Vos avis Google</div>
          <h2 id="avis-google-title" className="serif text-3xl text-[#F3E9D2]">Laissez votre avis</h2>
          <p className="mt-4 text-[#F3E9D2]/70 leading-relaxed">Vous avez partagé un moment avec moi ? Votre témoignage compte. Partagez votre expérience ou découvrez les avis des autres personnes.</p>
          <div className="mt-6 flex flex-col sm:flex-row justify-center gap-3">
            <a className="dl-btn dl-btn-solid justify-center" href="https://search.google.com/local/writereview?placeid=ChIJTdDtRUdK5kcR2jv0cbdO-Hc" target="_blank" rel="noopener noreferrer">Laisser un avis sur Google</a>
            <a className="dl-btn dl-btn-outline justify-center" href="https://www.google.com/search?q=dorothee+lancelot&amp;si=APenkKnzv9m99ToiohAuzpadUwbOz34nZJ3j2Ukmo5XOUYWAptYBkbwkkrbarl0fZqqZpSVQ4bTmO6AvBAD9h4LhsNDCGn4p73FuLmlRZoI3VcbXyuVAm1I%3D" target="_blank" rel="noopener noreferrer">Voir les autres avis</a>
          </div>
        </div>
      </section>
    </div>
  );
}
